// `priority` = 1 means highest priority.

import { BaseAttribute } from '@hyphengroup/cms-components/types/attributes';
import {
  AttrValue,
  getAttrFromProduct,
} from '@hyphengroup/cms-components/types/attrNames';
import { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import { VerticalType } from '@hyphengroup/cms-components/types/cmsTypes';
import { BrandId } from '@hyphengroup/cms-components/utils/override';

/**
 * Whether the v3 form serves personal loans in this market, and so carries
 * CMS-1959's loan summary and product wording. SingSaver only: MoneyHero
 * personal loans have no lead form, and EIR is an SG field.
 */
export const hasPersonalLoanLeadForm = (brand: BrandId): boolean =>
  brand === 'singsaver';

const configByVertical = (
  vertical: VerticalType,
  brand: BrandId,
): readonly PriorityAttribute[] | null => {
  switch (vertical) {
    case 'CREDIT_CARD':
      return ccPriorityConfig;
    case 'PERSONAL_LOANS':
      return hasPersonalLoanLeadForm(brand)
        ? plLoanInfoPriorityConfig
        : plPriorityConfig;
    case 'INVESTMENTS':
      return stkPriorityConfig;
    case 'BANKING':
      return bkaPriorityConfig;
    default:
      return null;
  }
};

export const getDisplayAttributeByVertical = (
  product: CmsProduct | undefined,
  vertical: VerticalType,
  brand: BrandId,
): BaseAttribute[] | null => {
  const config = configByVertical(vertical, brand);
  return config ? getDisplayAttribute(product, config) : null;
};

/**
 * The attribute names this card is configured to draw, in display order.
 * Exported so the page's calculated-attributes declaration asks the engine for
 * exactly what the card will render, rather than keeping a second list that
 * can drift from this one (CMS-1959).
 */
export const getPriorityAttributeNames = (
  vertical: VerticalType,
  brand: BrandId,
): AttrValue[] =>
  [...(configByVertical(vertical, brand) ?? [])]
    .sort((a, b) => a.priority - b.priority)
    .slice(0, MAX_DISPLAY_ATTRIBUTES)
    .map(({ attributeName }) => attributeName);

type PriorityAttribute = {
  attributeName: AttrValue;
  priority: number;
};

const MAX_DISPLAY_ATTRIBUTES = 2;

function getDisplayAttribute(
  product: CmsProduct | undefined,
  priorities: readonly PriorityAttribute[],
): BaseAttribute[] | null {
  if (!product) return null;

  const sorted = [...priorities].sort((a, b) => a.priority - b.priority);

  const found = sorted.reduce<BaseAttribute[]>((acc, { attributeName }) => {
    if (acc.length >= MAX_DISPLAY_ATTRIBUTES) return acc;
    const attr = getAttrFromProduct(attributeName, product);
    if (attr) acc.push(attr);
    return acc;
  }, []);

  return found.length > 0 ? found : null;
}

export const ccPriorityConfig: PriorityAttribute[] = [
  { attributeName: 'CARD_BENEFIT', priority: 1 },
  { attributeName: 'ANNUAL_FEE', priority: 2 },
  { attributeName: 'CASHBACK_LOCAL', priority: 3 },
  { attributeName: 'AIRMILES_LOCAL', priority: 4 },
  { attributeName: 'CASHBACK_OVERSEAS', priority: 5 },
];

export const plPriorityConfig: PriorityAttribute[] = [
  { attributeName: 'APR', priority: 1 },
  { attributeName: 'HANDLING_FEE', priority: 2 },
  { attributeName: 'ANNUAL_RATE', priority: 3 },
  { attributeName: 'TOTAL_INTEREST', priority: 4 },
  { attributeName: 'MONTHLY_REPAYMENT', priority: 5 },
];

/**
 * The SingSaver loan summary (CMS-1959). Both are calculated on this vertical:
 * the value comes from the engine, the attribute only carries the label and
 * the empty state.
 */
export const plLoanInfoPriorityConfig: PriorityAttribute[] = [
  { attributeName: 'ANNUAL_RATE', priority: 1 },
  { attributeName: 'APR', priority: 2 },
];

export const stkPriorityConfig: PriorityAttribute[] = [
  { attributeName: 'MIN_DEPOSIT', priority: 1 },
  { attributeName: 'MINIMUM_COMMISSION_US', priority: 2 },
  { attributeName: 'STOCKS_SG_MIN_COMMISSION', priority: 3 },
  { attributeName: 'MIN_TRADING_FEE', priority: 4 },
  { attributeName: 'MIN_TRADES_FOR_REWARDS', priority: 5 },
  { attributeName: 'MINIMUM_COMMISSION_INTERNET', priority: 6 },
  { attributeName: 'EQUITY_FUNDS_SWITCHING_FEE', priority: 7 },
  { attributeName: 'CRYPTO_CURRENCIES', priority: 8 },
];

export const bkaPriorityConfig: PriorityAttribute[] = [
  { attributeName: 'MIN_DEPOSIT', priority: 1 },
  { attributeName: 'INTEREST_RATE_SAVINGS', priority: 2 },
  { attributeName: 'MAX_INTEREST_RATE_SAVINGS', priority: 3 },
  { attributeName: 'MINIMUM_ASSET_UNDER_MANAGEMENT', priority: 4 },
  { attributeName: 'BASE_ANNUAL_INTEREST_RATE', priority: 5 },
  { attributeName: 'MAX_BONUS_INTEREST_RATE', priority: 6 },
  { attributeName: 'CASHBACK_REWARD', priority: 7 },
];
